import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import {
  Container,
  Title,
  Alert,
  Text,
  Loader,
  Table,
  Badge,
  Card,
  Stack,
  Group,
  Paper,
  SimpleGrid,
  SegmentedControl,
  Pagination,
  Button,
  Modal,
  NumberInput,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconExclamationCircle, IconCoins, IconCashBanknote } from '@tabler/icons-react';
import { useApi, useApiClient, useSession } from '../../api';
import { formatCents } from '../../components/products/productDisplay';

const PAGE_LIMIT = 20;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const StatTile = ({ label, value, valueColor }) => (
  <Paper withBorder p="md" radius="md">
    <Stack gap={2}>
      <Text size="xs" c="dimmed">{label}</Text>
      <Text size="xl" fw={700} c={valueColor}>{value}</Text>
    </Stack>
  </Paper>
);

// ── Payouts (contract §4.18) ────────────────────────────────────────────────
// Internal workflow: sellers request, admins pay manually out-of-band.

const PAYOUT_MIN_DOLLARS = 20;

const PAYOUT_STATUS = {
  requested: { color: 'yellow', label: 'Pending' },
  paid: { color: 'green', label: 'Paid' },
  rejected: { color: 'red', label: 'Rejected' },
};

const PayoutStatusBadge = ({ payout }) => {
  const meta = PAYOUT_STATUS[payout.status] || { color: 'gray', label: payout.status };
  const badge = (
    <Badge color={meta.color} variant="light">{meta.label}</Badge>
  );
  // Rejections carry the admin's note — surface it on hover.
  if (payout.status === 'rejected' && payout.admin_note) {
    return (
      <Tooltip label={payout.admin_note} multiline maw={320} withArrow>
        {badge}
      </Tooltip>
    );
  }
  return badge;
};

const PayoutsSection = () => {
  const api = useApiClient();
  const [page, setPage] = useState(1);
  // Envelope of PayoutRequest + top-level extras
  // {available_cents, requested_cents, paid_cents, payout_method}.
  const { data, loading, error, refetch } = useApi(
    '/api/v1/me/seller/payouts',
    { page, limit: PAGE_LIMIT },
    { query: { placeholderData: (prev) => prev } }
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [dollars, setDollars] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [methodDraft, setMethodDraft] = useState('');
  const [savingMethod, setSavingMethod] = useState(false);

  const payouts = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const availableCents = data?.available_cents ?? 0;
  const payoutMethod = data?.payout_method ?? null;
  // §4.22 — unreversed referral commissions already included in available_cents.
  const referralCommissionCents = data?.referral_commission_cents ?? 0;

  // Keep the editor prefilled with the saved method (refreshes on refetch).
  useEffect(() => {
    setMethodDraft(payoutMethod ?? '');
  }, [payoutMethod]);

  const amount = typeof dollars === 'number' ? dollars : parseFloat(dollars);
  const maxDollars = availableCents / 100;
  // §4.20: clawbacks can push the balance negative — nothing to request then.
  const canRequest = availableCents >= PAYOUT_MIN_DOLLARS * 100;
  const amountValid =
    Number.isFinite(amount) &&
    amount >= PAYOUT_MIN_DOLLARS &&
    Math.round(amount * 100) <= availableCents;
  const rangeMessage = `Payouts must be between $${PAYOUT_MIN_DOLLARS} and ${formatCents(availableCents)} (your available balance)`;

  const closeModal = () => {
    setModalOpen(false);
    setDollars('');
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (!payoutMethod || !amountValid || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.post('/api/v1/me/seller/payouts', {
        amount_cents: Math.round(amount * 100),
      });
      notifications.show({ color: 'green', message: 'Payout requested' });
      closeModal();
      refetch();
    } catch (err) {
      // Problem details (§4.18): 400 no payout method, 422 min/exceeds
      // available, 409 one open request already.
      let message = err?.detail || err?.message || 'Could not request the payout';
      if (err?.status === 409) message = 'You already have a pending request';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveMethod = async () => {
    const value = methodDraft.trim();
    if (!value || savingMethod) return;
    setSavingMethod(true);
    try {
      // SellerPrivate.payout_method, writable via PATCH /me/seller (§4.18).
      await api.patch('/api/v1/me/seller', { payout_method: value });
      notifications.show({ color: 'green', message: 'Payout method saved' });
      refetch();
    } catch (err) {
      notifications.show({
        color: 'red',
        message: err?.message || 'Failed to save payout method',
      });
    } finally {
      setSavingMethod(false);
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2} size="h3">Payouts</Title>
        <Button
          leftSection={<IconCashBanknote size="1rem" />}
          onClick={() => setModalOpen(true)}
          disabled={loading || !canRequest}
        >
          Request payout
        </Button>
      </Group>

      {error && (
        <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
          {error.message}
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm">
        <StatTile
          label="Available"
          value={formatCents(availableCents)}
          // Negative after a clawback (§4.20) — flag it, don't hide it.
          valueColor={availableCents < 0 ? 'red' : undefined}
        />
        <StatTile label="Requested" value={formatCents(data?.requested_cents ?? 0)} />
        <StatTile label="Paid out" value={formatCents(data?.paid_cents ?? 0)} />
      </SimpleGrid>

      {referralCommissionCents > 0 && (
        <Text size="sm" c="dimmed">
          Includes referral commissions: {formatCents(referralCommissionCents)}
        </Text>
      )}

      {/* Inline payout-method editor — keeps the whole flow on this page. */}
      <Group align="flex-end" gap="sm" wrap="nowrap">
        <TextInput
          label="Payout method"
          placeholder="PayPal: you@example.com"
          value={methodDraft}
          onChange={(e) => setMethodDraft(e.currentTarget.value)}
          maxLength={200}
          style={{ flex: 1, maxWidth: 420 }}
        />
        <Button
          variant="light"
          onClick={handleSaveMethod}
          loading={savingMethod}
          disabled={!methodDraft.trim() || methodDraft.trim() === payoutMethod}
        >
          Save method
        </Button>
      </Group>

      {payouts.length > 0 && (
        <Card shadow="sm" padding={0} radius="md" withBorder>
          <Table.ScrollContainer minWidth={480}>
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Amount</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {payouts.map((payout) => (
                  <Table.Tr key={payout.id}>
                    <Table.Td>{formatDate(payout.requested_at)}</Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>{formatCents(payout.amount_cents)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <PayoutStatusBadge payout={payout} />
                        {payout.status === 'rejected' && payout.admin_note && (
                          <Text size="xs" c="dimmed">{payout.admin_note}</Text>
                        )}
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}
      {!loading && payouts.length === 0 && (
        <Text c="dimmed" size="sm">No payout requests yet.</Text>
      )}

      {totalPages > 1 && (
        <Group justify="center">
          <Pagination value={page} onChange={setPage} total={totalPages} />
        </Group>
      )}

      <Modal
        opened={modalOpen}
        onClose={closeModal}
        title="Request a payout"
        centered
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          {payoutMethod ? (
            <Text size="sm">
              Payouts go to: <Text span fw={600}>{payoutMethod}</Text>
            </Text>
          ) : (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="yellow">
              Set your payout method below the balance tiles first — we need to
              know where to send the money.
            </Alert>
          )}

          <NumberInput
            label="Amount (USD)"
            value={dollars}
            onChange={setDollars}
            min={PAYOUT_MIN_DOLLARS}
            max={maxDollars}
            decimalScale={2}
            prefix="$"
            error={dollars !== '' && !amountValid ? rangeMessage : null}
          />
          <Text size="xs" c="dimmed">
            Available balance: {formatCents(availableCents)}. Minimum payout is
            ${PAYOUT_MIN_DOLLARS}. Payouts are processed manually by our team.
          </Text>

          {submitError && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {submitError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="light" color="gray" onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!payoutMethod || !amountValid}
              loading={submitting}
            >
              Submit request
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

// One EarningRecord row (contract §3.16) — itemized completed income.
// Deliberately payer-anonymous: the contract never exposes who tipped or
// bought, so no buyer identity is rendered here. Rows with `reversed_at`
// were clawed back (refund/chargeback, §4.20): they stay listed so the
// ledger explains payout-balance drops, flagged and de-emphasized.
const EarningRow = ({ record }) => {
  const reversed = !!record.reversed_at;
  return (
    <Table.Tr>
      <Table.Td>{formatDate(record.completed_at)}</Table.Td>
      <Table.Td>
        {record.type === 'tip' ? (
          <Badge color="pink" variant="light">Tip</Badge>
        ) : record.type === 'fan_sub' ? (
          <Badge color="teal" variant="light">Fan sub</Badge>
        ) : (
          <Badge color="grape" variant="light">Album sale</Badge>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" c={reversed ? 'dimmed' : undefined}>
            {record.type === 'tip'
              ? 'Tip received'
              : record.type === 'fan_sub'
                ? 'Fan subscription payment'
                : `Album sale: ${record.album?.title ?? '—'}`}
          </Text>
          {reversed && (
            <Badge color="red" variant="light">Reversed</Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Text
          size="sm"
          fw={600}
          c={reversed ? 'dimmed' : undefined}
          td={reversed ? 'line-through' : undefined}
        >
          {formatCents(record.amount_cents)}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
};

const EarningsLedger = () => {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('all');

  // Aggregate totals (contract §4.12) — the ledger below never re-sums.
  const { data: stats } = useApi('/api/v1/me/seller/stats');

  // Itemized completed income (contract §4.17). `type` is omitted for All.
  const { data, loading, error } = useApi(
    '/api/v1/me/seller/earnings',
    {
      page,
      limit: PAGE_LIMIT,
      ...(typeFilter !== 'all' ? { type: typeFilter } : {}),
    },
    { query: { placeholderData: (prev) => prev } }
  );
  const earnings = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  const handleFilterChange = (value) => {
    setTypeFilter(value);
    setPage(1);
  };

  return (
    <Stack gap="xl">
      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="sm">
        <StatTile
          label="Tips received"
          value={formatCents(stats?.tips_received_cents ?? 0)}
        />
        <StatTile
          label="Album sales"
          value={formatCents(stats?.album_sales_cents ?? 0)}
        />
        {/* Fan subscription aggregates (§4.12 additions) — rendered once the
            backend serves them */}
        {stats?.active_fan_subs != null && (
          <StatTile label="Active subscribers" value={stats.active_fan_subs} />
        )}
        {stats?.fan_sub_revenue_cents != null && (
          <StatTile
            label="Subscription revenue"
            value={formatCents(stats.fan_sub_revenue_cents)}
          />
        )}
      </SimpleGrid>

      <SegmentedControl
        value={typeFilter}
        onChange={handleFilterChange}
        data={[
          { value: 'all', label: 'All' },
          { value: 'tip', label: 'Tips' },
          { value: 'album_sale', label: 'Album sales' },
          { value: 'fan_sub', label: 'Subscriptions' },
        ]}
        w="fit-content"
      />

      {error && (
        <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
          {error.message}
        </Alert>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      ) : earnings.length === 0 ? (
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack align="center" gap="md" py="xl">
            <IconCoins size={48} color="var(--mantine-color-gray-4)" />
            <Text c="dimmed" size="lg">No earnings yet</Text>
            <Text c="dimmed" size="sm">
              Completed tips, album sales and fan subscription payments will show up here.
            </Text>
          </Stack>
        </Card>
      ) : (
        <Card shadow="sm" padding={0} radius="md" withBorder>
          <Table.ScrollContainer minWidth={560}>
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Amount</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {earnings.map((record) => (
                  <EarningRow key={`${record.type}-${record.id}`} record={record} />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      {totalPages > 1 && (
        <Group justify="center">
          <Pagination value={page} onChange={setPage} total={totalPages} />
        </Group>
      )}
    </Stack>
  );
};

const SellerEarningsPage = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const { isSeller, status } = useSession();

  // Wait for auth state to settle (Clerk loaded + session role known) before
  // redirecting — otherwise signed-in sellers deep-linking here bounce home.
  if (!isLoaded || status === 'connecting') {
    return (
      <Container size="xl" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/" replace />;
  }

  if (!isSeller) {
    return (
      <Container size="lg" py="xl">
        <Alert
          icon={<IconExclamationCircle size="1rem" />}
          color="pink"
          title="Princess Access Required"
        >
          <Text>
            You need to be a registered princess to access this page.
            Please register as a princess first to view your earnings.
          </Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Title order={1} mb="xl">My Earnings</Title>
      <Stack gap="xl">
        <PayoutsSection />
        <EarningsLedger />
      </Stack>
    </Container>
  );
};

export default SellerEarningsPage;
